<script setup lang="ts">
import { ConfirmationPopup } from '@metapic-apps/shared'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import { useGetCreatorIdentityRequest, useUpdateIdentityRequest } from '@/api/identity.api'
import IdentityForm from '@/components/IdentityForm/IdentityForm.vue'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import { serializeIdentity } from '@/lib/adapters/identity.adapter'
import { notification } from '@/lib/helpers'
import { useBackToPreviousPage } from '@/lib/hooks/back-to-previous-page.hooks'
import { type IdentityFormType } from '@/lib/types/identity.types'
import { useUserStore } from '@/stores/user'
import { BoxComponent, FlexComponent, LoadingComponent } from '@/ui-library'

const route = useRoute()
const userStore = useUserStore()

const creatorId = Number(route.params.id as string)
const handleBack = useBackToPreviousPage({ name: 'creators' })

const { data, isLoading, getCreator } = useGetCreatorIdentityRequest(creatorId)
const { updateIdentity, isLoading: isEditingLoading, error } = useUpdateIdentityRequest(creatorId)

const isConfirmationPopupOpen = ref(false)
const pendingFormData = ref<IdentityFormType | null>(null)

const onIdentityEdit = (formData: IdentityFormType) => {
  pendingFormData.value = formData
  isConfirmationPopupOpen.value = true
}

const confirmUpdate = async () => {
  if (!pendingFormData.value) {
    return
  }

  const payload = serializeIdentity(pendingFormData.value)
  await updateIdentity(payload)

  if (!error.value) {
    await getCreator()
    notification.success('Identity was updated successfully')
  }

  isConfirmationPopupOpen.value = false
}
</script>

<template>
  <HeaderComponent />

  <LoadingComponent v-if="userStore.isLoading" :size="64" :is-full-size="true" />

  <template v-if="userStore.user">
    <FlexComponent class="identity-editing__wrapper" justify="center">
      <BoxComponent w="600">
        <IdentityForm
          v-if="data"
          :creator-id="creatorId"
          :identity="data"
          :is-loading="isLoading || isEditingLoading"
          :is-edit-mode="true"
          @handle-submit="onIdentityEdit"
          @handle-back="handleBack"
        />
      </BoxComponent>
    </FlexComponent>
  </template>

  <ConfirmationPopup
    v-model="isConfirmationPopupOpen"
    title="Identity update"
    :description="`Are you sure you want to update the identity for creator ${creatorId}?`"
    submit-btn-text="Update"
    :is-loading="isEditingLoading"
    submit-btn-variant="primary"
    @on-submit="confirmUpdate"
  />
</template>

<style scoped lang="scss">
.identity-editing__wrapper {
  max-width: 1320px;
  margin: 0 auto;
  padding: 40px 20px;
}
</style>
